import { ZODIAC_SIGNS } from "@/data/zodiac";
import { NORTH_HOUSE_CENTRES, PLANET_ABBREV, signForHouse, signIndex } from "./geometry";

export interface ChartGraha {
  readonly planet: string;
  readonly house: number;
  readonly retrograde: boolean;
}

interface VedicChartProps {
  readonly ascendantSign: string;
  readonly placements: readonly ChartGraha[];
  readonly title?: string;
}

const SIDE = 300;

/**
 * A rasi or varga chart in the North Indian convention, as SVG. Draws only
 * what it is given: sign, house and retrograde state all arrive from the
 * engine. It never computes a placement.
 */
export function VedicChart({ ascendantSign, placements, title = "Birth chart" }: VedicChartProps) {
  const asc = Math.max(0, signIndex(ascendantSign));
  const byHouse = new Map<number, ChartGraha[]>();
  for (const p of placements) {
    byHouse.set(p.house, [...(byHouse.get(p.house) ?? []), p]);
  }
  const s = SIDE;
  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      role="img"
      aria-label={`${title}: ${ascendantSign} rising`}
      className="h-auto w-full text-ink"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1">
        <rect x="0.5" y="0.5" width={s - 1} height={s - 1} />
        <line x1="0" y1="0" x2={s} y2={s} />
        <line x1={s} y1="0" x2="0" y2={s} />
        <path d={`M ${s / 2} 0 L ${s} ${s / 2} L ${s / 2} ${s} L 0 ${s / 2} Z`} />
      </g>
      {NORTH_HOUSE_CENTRES.map((centre, index) => {
        const house = index + 1;
        const sign = signForHouse(asc, house);
        const grahas = byHouse.get(house) ?? [];
        const cx = centre.x * s;
        const cy = centre.y * s;
        return (
          <g key={house}>
            <text
              x={cx}
              y={cy - s * 0.055}
              textAnchor="middle"
              className="fill-mid font-mono"
              fontSize="9"
              aria-label={`House ${house}, ${ZODIAC_SIGNS[sign]}`}
            >
              {sign + 1}
              {house === 1 ? " ·" : ""}
            </text>
            {chunk(grahas, 2).map((pair, line) => (
              <text
                key={line}
                x={cx}
                y={cy + s * 0.02 + line * 12}
                textAnchor="middle"
                className="fill-ink font-mono"
                fontSize="10.5"
              >
                {pair.map(label).join(" ")}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function label(graha: ChartGraha): string {
  const short = PLANET_ABBREV[graha.planet] ?? graha.planet.slice(0, 2);
  return graha.retrograde ? `${short}ᴿ` : short;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
